import { supabase } from './supabase';
import type { User } from '@supabase/supabase-js';
import { useStore } from './store';

// Check if Supabase is properly configured
const isSupabaseConfigured = () => {
  const url = import.meta.env.VITE_SUPABASE_URL as string;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  return url && key && 
         url !== 'https://placeholder.supabase.co' && 
         key !== 'placeholder-key';
};

export interface AuthState {
  user: User | null;
  loading: boolean;
}

export const authService = {
  async signIn(email: string, password: string) {
    if (!isSupabaseConfigured()) {
      const demoUsers = {
        'admin@medsystem.com': { role: 'admin', name: 'Admin User' },
        'doctor@medsystem.com': { role: 'doctor', name: 'Dr. Demo' },
        'nurse@medsystem.com': { role: 'nurse', name: 'Nurse Demo' },
        'receptionist@medsystem.com': { role: 'receptionist', name: 'Reception Demo' },
        'pharmacist@medsystem.com': { role: 'pharmacist', name: 'Pharmacist Demo' },
        'demo@medsystem.com': { role: 'admin', name: 'Demo User' },
      };

      const demoUser = demoUsers[email as keyof typeof demoUsers];
      if (demoUser && password === 'demo123') {
        const mockUser: User = {
          id: 'mock-user-id',
          email,
          user_metadata: { name: demoUser.name, role: demoUser.role },
          app_metadata: { role: demoUser.role },
          aud: 'authenticated',
          created_at: new Date().toISOString(),
          role: 'authenticated',
          updated_at: new Date().toISOString(),
          email_confirmed_at: new Date().toISOString(),
          last_sign_in_at: new Date().toISOString(),
          phone: '',
          confirmation_sent_at: '',
          confirmed_at: new Date().toISOString(),
          recovery_sent_at: '',
          new_email: '',
          invited_at: '',
          action_link: '',
          email_change_sent_at: '',
          new_phone: '',
          phone_confirmed_at: '',
          identities: []
        };
        
        localStorage.setItem('mock_auth_user', JSON.stringify(mockUser));
        useStore.getState().setUser(mockUser);
        return { user: mockUser, session: null };
      } else {
        throw new Error('Invalid credentials. Use admin@medsystem.com, doctor@medsystem.com, nurse@medsystem.com, receptionist@medsystem.com, or pharmacist@medsystem.com with password demo123');
      }
    }
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    return data;
  },

  async signUp(email: string, password: string) {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase not configured. Please set up your Supabase credentials.');
    }
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    if (error) throw error;
    return data;
  },

  async signOut() {
    if (!isSupabaseConfigured()) {
      localStorage.removeItem('mock_auth_user');
      useStore.getState().setUser(null);
      return;
    }
    
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async resetPassword(email: string) {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase not configured. Password reset requires Supabase setup.');
    }
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });
    
    if (error) throw error;
  },

  async getCurrentUser() {
    if (!isSupabaseConfigured()) {
      const mockUserStr = localStorage.getItem('mock_auth_user');
      if (mockUserStr) {
        const mockUser = JSON.parse(mockUserStr);
        useStore.getState().setUser(mockUser);
        return mockUser;
      }
      return null;
    }
    
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  },

  onAuthStateChange(callback: (user: User | null) => void) {
    if (!isSupabaseConfigured()) {
      const mockUserStr = localStorage.getItem('mock_auth_user');
      const mockUser = mockUserStr ? JSON.parse(mockUserStr) : null;
      callback(mockUser);
      return { data: { subscription: { unsubscribe: () => {} } } };
    }
    
    return supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user ?? null);
    });
  }
};
